import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Loader2, Clock, AlertTriangle, CheckCircle2, XCircle, Award } from 'lucide-react';
import { BACKEND_URL } from '../../config/canonical';

const apiCall = async (path, opts = {}) => {
  const token = localStorage.getItem('nexus_token');
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

const ExamPlayer = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const examStartedRef = useRef(false);

  const start = useCallback(async () => {
    if (examStartedRef.current) return;
    examStartedRef.current = true;
    try {
      const r = await apiCall(`/api/training-center/courses/${courseId}/exam/start`, { method: 'POST' });
      setExam(r);
      setSecondsLeft((r.time_limit_min || 60) * 60);
    } catch (e) {
      toast.error(e.message);
      examStartedRef.current = false;
      setTimeout(() => navigate(`/training-center/course/${courseId}`), 1500);
    }
  }, [courseId, navigate]);
  useEffect(() => { start(); }, [start]);

  useEffect(() => {
    if (!exam || result) return;
    const t = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [exam, result]);

  // Auto-submit on timeout
  useEffect(() => {
    if (exam && !result && secondsLeft === 0) {
      handleSubmit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, exam, result]);

  const minLeft = Math.floor(secondsLeft / 60);
  const secLeft = String(secondsLeft % 60).padStart(2, '0');

  const handleSubmit = async (force = false) => {
    if (!exam) return;
    if (!force) {
      const unanswered = exam.questions.filter((_, i) => answers[i] === undefined).length;
      if (unanswered > 0) {
        setConfirmOpen(true);
        return;
      }
    }
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const ans = exam.questions.map((_, i) => answers[i] ?? -1);
      const r = await apiCall(`/api/training-center/courses/${courseId}/exam/submit`, {
        method: 'POST',
        body: JSON.stringify({ session_id: exam.session_id, answers: ans }),
      });
      setResult(r.attempt);
      if (r.attempt.passed) toast.success(`Passed · ${r.attempt.score_pct}%`);
      else toast.error(`Did not pass · ${r.attempt.score_pct}%`);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  if (!exam) {
    return <div className="min-h-screen bg-[#0A0F1A] text-white p-10 font-mono text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Generating randomized exam…</div>;
  }

  // Defensive empty-pool guard. Backend now refuses to start a 0-question
  // exam, but if a course slips through (or a session document is stale)
  // we render a clear actionable message instead of a blank black screen.
  if (!exam.questions || exam.questions.length === 0) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] text-white px-4 py-10 flex items-center justify-center" data-testid="exam-empty-pool">
        <Card className="border-2 border-amber-500/40 bg-amber-500/5 max-w-md w-full">
          <CardContent className="p-8 text-center space-y-3">
            <AlertTriangle className="w-12 h-12 text-amber-300 mx-auto" />
            <h1 className="text-xl font-bold text-white">Exam not available yet</h1>
            <p className="text-sm text-slate-300 leading-relaxed">
              The question bank for <span className="font-mono text-amber-300">{courseId}</span> hasn&apos;t been published yet.
              Please contact your training administrator. No attempt has been recorded against your record.
            </p>
            <Button onClick={() => navigate(`/training-center/course/${courseId}`)} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="exam-empty-back">
              Back to course
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-[#0A0F1A] text-white" data-testid="exam-result-page">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <Card className={`border-2 ${result.passed ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-rose-500/50 bg-rose-500/5'}`} data-testid="exam-result-card">
            <CardContent className="p-8 text-center">
              {result.passed ? (
                <>
                  <CheckCircle2 className="w-16 h-16 text-emerald-300 mx-auto" />
                  <h1 className="text-3xl font-extrabold text-white mt-3">You passed.</h1>
                  <p className="text-slate-300 mt-1">{result.correct_count} / {result.total} correct · <span className="font-bold text-emerald-300" data-testid="exam-final-score">{result.score_pct}%</span></p>
                  <p className="text-xs text-slate-400 font-mono mt-3">Attempt {result.id} · Submitted {new Date(result.submitted_at).toLocaleString()}</p>
                </>
              ) : (
                <>
                  <XCircle className="w-16 h-16 text-rose-300 mx-auto" />
                  <h1 className="text-3xl font-extrabold text-white mt-3">Did not pass.</h1>
                  <p className="text-slate-300 mt-1">{result.correct_count} / {result.total} · <span className="font-bold text-rose-300" data-testid="exam-final-score">{result.score_pct}%</span></p>
                  <p className="text-xs text-slate-400 font-mono mt-3">Need {result.passing_score}% — retake available after 24-hour cooldown.</p>
                </>
              )}
              <div className="flex gap-2 justify-center mt-6 flex-wrap">
                <Button onClick={() => navigate(`/training-center/course/${courseId}`)} variant="outline" className="border-slate-500 !text-slate-200 hover:bg-slate-800" data-testid="exam-back-btn">
                  Back to course
                </Button>
                {result.passed && (
                  <Button onClick={() => navigate('/training-center')} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="exam-cert-btn">
                    <Award className="w-4 h-4 mr-1.5" /> View certifications
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0F1A] text-white" data-testid="exam-player-page">
      <div className="sticky top-0 z-20 border-b border-[#1E293B] bg-[#0F1B33]/95 backdrop-blur">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.18em] text-amber-300">FINAL EXAM</p>
            <h1 className="text-lg font-bold text-white">{courseId}</h1>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase text-slate-400">Time remaining</p>
            <p className={`text-2xl font-bold font-mono ${secondsLeft < 300 ? 'text-rose-300' : 'text-amber-300'}`} data-testid="exam-timer">
              {minLeft}:{secLeft}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4 pb-48" data-testid="exam-questions">
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            <strong className="text-amber-100">{exam.questions.length} questions · need {exam.passing_score}% to pass.</strong> The timer auto-submits at zero. Don&apos;t refresh the page — the session ID is locked to this attempt.
          </p>
        </div>

        {exam.questions.map((q, i) => (
          <Card key={q.n} className="bg-[#131A2A] border-[#1E293B]" data-testid={`exam-q-${q.n}`}>
            <CardContent className="p-5">
              <div className="flex items-start gap-3">
                <Badge className="bg-slate-800 border border-slate-600 text-amber-300 font-mono text-[11px] flex-shrink-0">{q.n}</Badge>
                <p className="text-sm text-white font-medium leading-relaxed">{q.q}</p>
              </div>
              <div className="space-y-1.5 mt-3 ml-9">
                {q.options.map((opt, j) => {
                  const picked = answers[i] === j;
                  return (
                    <label key={j} className={`flex items-start gap-2 px-2 py-1.5 rounded text-sm cursor-pointer transition ${
                      picked ? 'bg-amber-500/15 border border-amber-500/40 text-white'
                             : 'border border-transparent hover:bg-slate-800/60 text-slate-300'
                    }`}>
                      <input type="radio" name={`q-${i}`} className="mt-1 accent-amber-400"
                             checked={picked}
                             onChange={() => setAnswers({ ...answers, [i]: j })}
                             data-testid={`exam-q-${q.n}-opt-${j}`} />
                      <span className="min-w-0 flex-1">{opt}</span>
                    </label>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="fixed left-0 right-0 z-30 border-t border-[#1E293B] bg-[#0F1B33]/98 backdrop-blur exam-submit-bar"
           style={{ bottom: 'var(--nx-exam-bottom-offset, 60px)' }}
           data-testid="exam-submit-bar">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-slate-400 font-mono">
            <span data-testid="exam-answered-count">{Object.keys(answers).length}</span> / {exam.questions.length} answered
          </p>
          <Button onClick={() => handleSubmit(false)} disabled={submitting}
            className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
            data-testid="exam-submit-btn">
            {submitting ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Submitting…</> : 'Submit exam'}
          </Button>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="bg-[#0F1B33] border-amber-500/40 text-white">
          <DialogHeader>
            <DialogTitle className="text-amber-200 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Unanswered questions</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-300">
            You have {exam.questions.length - Object.keys(answers).length} unanswered question(s). They will be marked incorrect. Submit anyway?
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} className="border-slate-600 !text-slate-200">Keep working</Button>
            <Button onClick={() => handleSubmit(true)} className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold" data-testid="exam-confirm-submit">Submit anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExamPlayer;
